/**
 * Compact Budget / Committed / Available hint for spend line editors.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Space, Tag, Typography } from 'antd';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export type BudgetAvailabilityHintProps = {
  projectId?: string | null;
  wbsNodeId?: string | null;
  cbsCodeId?: string | null;
  costCategory?: string | null;
  /** When set, compare this spend to Available and mark Over */
  requestedAmount?: number | null;
  /** Single availability tag for dense table cells */
  compact?: boolean;
};

export const BudgetAvailabilityHint: React.FC<BudgetAvailabilityHintProps> = ({
  projectId,
  wbsNodeId,
  cbsCodeId,
  costCategory,
  requestedAmount,
  compact,
}) => {
  const enabled = !!projectId && !!wbsNodeId;
  const q = useQuery({
    queryKey: ['cost-control-availability', projectId, wbsNodeId, cbsCodeId, costCategory],
    queryFn: () =>
      projectService.getCostControlAvailability(String(projectId), {
        wbsNodeId: wbsNodeId || undefined,
        cbsCodeId: cbsCodeId || undefined,
        costCategory: costCategory || undefined,
      }),
    enabled,
    staleTime: 15_000,
  });

  if (!enabled) return null;
  if (q.isLoading) {
    return compact ? null : (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Checking budget…
      </Text>
    );
  }
  if (q.isError || !q.data) return null;

  const d = q.data as any;
  if (!d.active) {
    if (compact) return null;
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Cost control inactive (no approved budget)
      </Text>
    );
  }
  if (d.unmapped) {
    return <Tag color="error">{compact ? 'Unmapped' : 'No matching budget line'}</Tag>;
  }

  const available = Number(d.available) || 0;
  const requested = Number(requestedAmount);
  const hasRequested = Number.isFinite(requested) && requested > 0;
  const over = hasRequested ? Math.round((requested - available) * 100) / 100 : 0;
  const exceeds = hasRequested && over > 0.009;

  if (compact) {
    if (exceeds) {
      return (
        <Tag color="error" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
          Over {formatMoney(over)}
        </Tag>
      );
    }
    return (
      <Tag
        color={available < 0 ? 'error' : available < 1 ? 'warning' : 'success'}
        style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}
      >
        Avl {formatMoney(available)}
      </Tag>
    );
  }

  return (
    <Space size={4} wrap style={{ fontSize: 12 }}>
      <Tag>Bud {formatMoney(Number(d.budgeted) || 0)}</Tag>
      <Tag>Cmt {formatMoney(Number(d.committed) || 0)}</Tag>
      <Tag color={available < 0 ? 'error' : available < 1 ? 'warning' : 'success'}>
        Avl {formatMoney(available)}
      </Tag>
      {exceeds ? (
        <Tag color="error">Over {formatMoney(over)}</Tag>
      ) : hasRequested ? (
        <Tag color="success">OK for line</Tag>
      ) : null}
    </Space>
  );
};

export default BudgetAvailabilityHint;
